import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import CustomControl from '../../../../../../../../../../../../components/global/CustomControl';
import { IMAGE_SIZE_CODE } from '../../../../../../../../../../../../constants/image-size-codes';
import { getThumbBySize } from '../../../../../../../../../../../../util/photo';
import WatermarkOverlay from '../../../../../../../../../../../../components/global/WatermarkOverlay';
import { IMAGE_BLUR_TYPES } from '../../../../../../../../../../../../constants/image-blur-types';
import { MATCHED_PHOTO_WATERMARK_MODE } from '../../../../../../../../../../../../constants/pro-settings';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
      }),
    ),
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  isEntitled: PropTypes.bool,
  isLocked: PropTypes.bool,
  className: PropTypes.string,
  isSingleGrid: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PhotoSelect = ({
  photo,
  onSelect,
  selected,
  isEntitled,
  isLocked,
  className,
  isSingleGrid,
  watermarkSettings,
}) => {
  const foregroundThumbnails = useMemo(() => {
    if (!photo?.foreground?.original_url) {
      return [];
    }

    if (
      !isEntitled &&
      watermarkSettings?.watermarkMode ===
        MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (
      !isEntitled &&
      photo?.faceblocked_thumbnail_urls?.find(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      )
    ) {
      return photo?.faceblocked_thumbnail_urls?.filter(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      );
    }

    if (
      !isEntitled &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls;
    }

    return [photo?.foreground?.original_url];
  }, [photo, isEntitled]);

  const thumbnail = useMemo(() => {
    if (foregroundThumbnails.length > 0) {
      return foregroundThumbnails[0].url;
    }

    if (!isEntitled && photo.faceblocked_thumbnail_urls?.length > 0) {
      return photo.faceblocked_thumbnail_urls?.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
      )?.[0]?.url;
    }

    return getThumbBySize(photo.thumbnail_urls, IMAGE_SIZE_CODE.MEDIUM)?.url;
  }, [photo, isEntitled]);

  const classes = classNames({
    'digital-bundle-photo-select': true,
    'is-selected': selected,
    'is-entitled': isEntitled || isLocked,
    [className]: !!className,
  });

  const tooltipMessage = (() => {
    if (isEntitled) {
      return 'You already own this photo';
    }

    if (isLocked) {
      return 'This photo is already in your cart';
    }

    return undefined;
  })();

  return (
    <a
      className={classes}
      href="#"
      data-for={`entitled-photo-tooltip-${photo.id}`}
      data-tip={tooltipMessage}
      data-effect="solid"
      onClick={event => {
        event.preventDefault();
        onSelect(photo);
      }}
    >
      {(isEntitled || isLocked) && (
        <ReactTooltip
          id={`entitled-photo-tooltip-${photo.id}`}
          className="tooltip"
        />
      )}
      {!isEntitled && !isLocked && (
        <CustomControl
          id={photo.id}
          label="Photo Selected"
          labelSrOnly
          type="checkbox"
          checked={!!selected}
          size={isSingleGrid ? 'lg' : 'md'}
          readOnly
        />
      )}

      <div>
        <img src={thumbnail} alt="" className="img-block" />
        {!isEntitled && !photo?.foreground?.original_url && (
          <div className="watermark-container">
            <WatermarkOverlay
              text={watermarkSettings?.wallpaperCustomText}
              fontSize={watermarkSettings?.wallpaperFontSize}
              wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
              imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
            />
          </div>
        )}
      </div>
    </a>
  );
};

PhotoSelect.propTypes = propTypes;

export default PhotoSelect;
